import React, { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronRight,
  Clipboard,
  FileText,
  Images,
  KanbanSquare,
  MessageCircle,
  ReceiptText,
  Send,
  Users,
  UserPlus,
  X,
} from "lucide-react";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";
import { isLeadUnlocked, LOCKED_CUSTOMER_LABEL } from "../../utils/leadAccess";
import { playNotificationSound } from "../../utils/notificationSound";
import { formatMoneyDisplay } from "../../utils/money";

function quoteStatus(lead) {
  return String(lead?.quote?.status || "").toLowerCase();
}

function isDraftQuote(lead) {
  return quoteStatus(lead) === "draft";
}

function isClosedLead(lead) {
  const status = String(lead?.status || "").toLowerCase();
  return status === "declined" || status === "done" || !!lead?.archived_at;
}

function leadName(lead, businessId, unlockedLeadIds) {
  if (!lead) return LOCKED_CUSTOMER_LABEL;
  if (!isLeadUnlocked(lead, businessId, unlockedLeadIds)) {
    return LOCKED_CUSTOMER_LABEL;
  }
  return lead.customer || lead.form?.fullName || LOCKED_CUSTOMER_LABEL;
}

function leadMeta(lead) {
  return [lead?.trade, lead?.type || lead?.form?.projectType, lead?.suburb]
    .filter(Boolean)
    .join(" - ");
}

function leadDate(lead) {
  const raw = lead?.createdAt || lead?.created_at;
  if (!raw) return "";
  return new Date(raw).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
  });
}

function leadTime(lead) {
  const raw = lead?.createdAt || lead?.created_at || 0;
  return raw ? new Date(raw).getTime() || 0 : 0;
}

function leadEmail(lead) {
  return (
    lead?.form?.email ||
    lead?.email ||
    lead?.customer_email ||
    lead?.captured_info?.email ||
    lead?.lead_details?.email ||
    ""
  );
}

function leadPhone(lead) {
  return (
    lead?.form?.phone ||
    lead?.phone ||
    lead?.customer_phone ||
    lead?.captured_info?.phone ||
    lead?.lead_details?.phone ||
    ""
  );
}

function messagePreview(message) {
  if (!message) return "";
  if (message.body) return message.body;
  if (message.file_name) return message.file_name;
  return "Media message";
}

function LauncherTile({ icon: Icon, title, sub, badge, tone = "default", onClick }) {
  return (
    <button type="button" className={`rq-launcher-tile ${tone}`} onClick={onClick}>
      <span className="rq-launcher-tile__icon">
        <Icon size={26} />
      </span>
      {badge ? <span className="rq-launcher-tile__badge">{badge}</span> : null}
      <span className="rq-launcher-tile__title">{title}</span>
      <span className="rq-launcher-tile__sub">{sub}</span>
    </button>
  );
}

function ActionSheet({ title, subtitle, onClose, children }) {
  return (
    <div className="rq-launcher-sheet" role="dialog" aria-modal="true" aria-label={title}>
      <div className="rq-launcher-sheet__scrim" onClick={onClose} />
      <div className="rq-launcher-sheet__panel">
        <div className="rq-launcher-sheet__head">
          <div>
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rq-icon-button">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EmptySheetState({ title, body }) {
  return (
    <div className="rq-empty-sheet">
      <strong>{title}</strong>
      <span>{body}</span>
    </div>
  );
}

function JobRow({ lead, businessId, unlockedLeadIds, title, sub, right, onClick }) {
  return (
    <button type="button" className="rq-job-row" onClick={onClick}>
      <span className="rq-job-row__main">
        <strong>{title || leadName(lead, businessId, unlockedLeadIds)}</strong>
        <small>{sub || leadMeta(lead)}</small>
      </span>
      {right ? <span className="rq-job-row__right">{right}</span> : null}
      <ChevronRight size={18} />
    </button>
  );
}

export default function TodayScreen({
  profile,
  requests,
  unlockedLeadIds,
  businessId,
  createOnsiteQuoteLead,
  onGoToLead,
  onGoToLeadQuote,
  onGoToLeadMessages,
  onGoToLeadInvoice,
  onGoToLeadMedia,
  onSwitchToPipeline,
  isRead = () => false,
  markAsRead,
}) {
  const [activeSheet, setActiveSheet] = useState(null);
  const [quoteSheetTab, setQuoteSheetTab] = useState("ready");
  const [paperworkTab, setPaperworkTab] = useState("quotes");
  const [threadMessages, setThreadMessages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [linkCopied, setLinkCopied] = useState(false);
  const [creatingQuoteLead, setCreatingQuoteLead] = useState(false);
  const [instantQuoteStep, setInstantQuoteStep] = useState(0);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const { showToast } = useToast();
  const defaultTrade = useMemo(
    () =>
      Array.isArray(profile?.trade_types) && profile.trade_types[0]
        ? profile.trade_types[0]
        : "Painting",
    [profile]
  );
  const [instantQuoteForm, setInstantQuoteForm] = useState(() => ({
    customer: "",
    phone: "",
    email: "",
    suburb: "",
    postcode: "",
    trade: defaultTrade,
    address: "",
    quoteFor: [],
    progressPayments: false,
    cashPayment: false,
    notes: "",
  }));
  const [customerForm, setCustomerForm] = useState(() => ({
    customer: "",
    phone: "",
    email: "",
    address: "",
    suburb: "",
    postcode: "",
    trade: defaultTrade,
    notes: "",
  }));

  useEffect(() => {
    setInstantQuoteForm((prev) =>
      prev.trade ? prev : { ...prev, trade: defaultTrade }
    );
    setCustomerForm((prev) =>
      prev.trade ? prev : { ...prev, trade: defaultTrade }
    );
  }, [defaultTrade]);

  const accessibleLeads = useMemo(
    () =>
      (requests || []).filter((lead) =>
        isLeadUnlocked(lead, businessId, unlockedLeadIds)
      ),
    [requests, businessId, unlockedLeadIds]
  );
  const accessibleLeadIds = useMemo(
    () => accessibleLeads.map((lead) => lead.id).filter(Boolean),
    [accessibleLeads]
  );
  const accessibleLeadKey = accessibleLeadIds.join("|");

  useEffect(() => {
    if (!accessibleLeadIds.length) {
      setThreadMessages([]);
      return undefined;
    }
    let cancelled = false;
    supabase
      .from("lead_messages")
      .select("id, lead_id, sender_name, sender_role, body, file_name, file_type, created_at, read_at")
      .in("lead_id", accessibleLeadIds)
      .order("created_at", { ascending: false })
      .limit(200)
      .then(({ data }) => {
        if (!cancelled) setThreadMessages(data || []);
      });
    return () => {
      cancelled = true;
    };
  }, [accessibleLeadKey, accessibleLeadIds]);

  useEffect(() => {
    if (!businessId) {
      setInvoices([]);
      return undefined;
    }
    let cancelled = false;
    supabase
      .from("invoices")
      .select("id, lead_id, invoice_number, status, customer_status, total, sent_at, paid_at, created_at, updated_at")
      .eq("business_id", businessId)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (!cancelled) setInvoices(data || []);
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  useEffect(() => {
    if (!businessId) return undefined;
    const channel = supabase
      .channel(`new-lead-banner:${businessId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "leads",
          filter: `direct_business_id=eq.${businessId}`,
        },
        (payload) => {
          const customer =
            payload?.new?.customer_name || payload?.new?.suburb || "A customer";
          showToast(`New job request from ${customer}. Open Job board.`, "info");
          playNotificationSound();
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [businessId, showToast]);

  const readyQuoteLeads = accessibleLeads.filter(
    (lead) => !isClosedLead(lead) && !lead.quote
  );
  const draftQuoteLeads = accessibleLeads.filter((lead) => isDraftQuote(lead));
  const sentQuoteLeads = accessibleLeads.filter(
    (lead) => lead.quote && !isDraftQuote(lead)
  );
  const mediaJobs = accessibleLeads.filter((lead) => {
    const customerPhotos = Math.max(
      Number(lead.photos || 0),
      Number(lead.form?.uploadedPhotos?.length || 0)
    );
    const notesPhotos = Array.isArray(lead.tradieNotesPhotos)
      ? lead.tradieNotesPhotos.length
      : 0;
    return customerPhotos + notesPhotos > 0;
  });
  const activeJobCount = accessibleLeads.filter((lead) => !isClosedLead(lead)).length;
  const openInvoiceRows = invoices.filter(
    (invoice) => String(invoice.status || "").toLowerCase() !== "paid" && !invoice.paid_at
  );
  const paperworkAlertCount = draftQuoteLeads.length + openInvoiceRows.length;
  const invoiceRowsWithLead = invoices.map((invoice) => ({
    invoice,
    lead: requests.find((lead) => lead.id === invoice.lead_id),
  }));

  const messageThreads = useMemo(() => {
    const latest = new Map();
    for (const message of threadMessages) {
      if (!message?.lead_id || latest.has(message.lead_id)) continue;
      const lead = requests.find((item) => item.id === message.lead_id);
      if (lead) latest.set(message.lead_id, { message, lead });
    }
    return Array.from(latest.values());
  }, [threadMessages, requests]);

  const unreadThreads = messageThreads.filter(
    ({ lead, message }) => message.sender_role !== "tradie" && !isRead(lead.id)
  );

  const customerRows = useMemo(() => {
    const byCustomer = new Map();
    for (const lead of accessibleLeads) {
      const name = leadName(lead, businessId, unlockedLeadIds);
      const email = leadEmail(lead);
      const phone = leadPhone(lead);
      const isUnnamed = name === LOCKED_CUSTOMER_LABEL;
      const key = isUnnamed
        ? `lead:${lead.id}`
        : (email || phone || name).toLowerCase().trim();
      if (!key) continue;

      const existing = byCustomer.get(key) || {
        key,
        name,
        email,
        phone,
        jobs: [],
        latestLead: lead,
      };
      existing.jobs.push(lead);
      if (leadTime(lead) > leadTime(existing.latestLead)) {
        existing.latestLead = lead;
      }
      if (!existing.email && email) existing.email = email;
      if (!existing.phone && phone) existing.phone = phone;
      if (existing.name === LOCKED_CUSTOMER_LABEL && name !== LOCKED_CUSTOMER_LABEL) {
        existing.name = name;
      }
      byCustomer.set(key, existing);
    }
    return Array.from(byCustomer.values()).sort(
      (a, b) => leadTime(b.latestLead) - leadTime(a.latestLead)
    );
  }, [accessibleLeads, businessId, unlockedLeadIds]);

  const shareLink = businessId
    ? `${(process.env.REACT_APP_APP_URL || window.location.origin).replace(/\/$/, "")}?tradie=${businessId}`
    : "";
  const shareCopy = encodeURIComponent(
    `Hi, send your job details here and I will quote it: ${shareLink}`
  );

  function closeSheet() {
    setInstantQuoteStep(0);
    setActiveSheet(null);
  }

  function openLeadQuote(leadId) {
    closeSheet();
    onGoToLeadQuote?.(leadId);
  }

  function openLeadMessages(leadId) {
    closeSheet();
    markAsRead?.(leadId);
    onGoToLeadMessages?.(leadId);
  }

  function openLeadInvoice(leadId) {
    closeSheet();
    onGoToLeadInvoice?.(leadId);
  }

  function openLeadMedia(leadId) {
    closeSheet();
    onGoToLeadMedia?.(leadId);
  }

  function openLeadOverview(leadId) {
    closeSheet();
    onGoToLead?.(leadId);
  }

  function updateInstantQuoteField(field, value) {
    setInstantQuoteForm((prev) => ({ ...prev, [field]: value }));
  }

  function updateCustomerField(field, value) {
    setCustomerForm((prev) => ({ ...prev, [field]: value }));
  }

  function resetInstantQuoteForm() {
    setInstantQuoteStep(0);
    setInstantQuoteForm({
      customer: "",
      phone: "",
      email: "",
      suburb: "",
      postcode: "",
      trade: defaultTrade,
      address: "",
      quoteFor: [],
      progressPayments: false,
      cashPayment: false,
      notes: "",
    });
  }

  function startNewCustomerQuote() {
    resetInstantQuoteForm();
    setActiveSheet("instantQuote");
  }

  function startExistingCustomerQuote(customer) {
    const latest = customer.latestLead || {};
    setInstantQuoteForm({
      customer: customer.name === LOCKED_CUSTOMER_LABEL ? "" : customer.name || "",
      phone: customer.phone || leadPhone(latest),
      email: customer.email || leadEmail(latest),
      address: latest.form?.address || "",
      suburb: latest.form?.suburb || latest.suburb || "",
      postcode: latest.form?.postcode || latest.postcode || "",
      trade: latest.form?.trade || latest.trade || defaultTrade,
      quoteFor: [],
      progressPayments: false,
      cashPayment: false,
      notes: "",
    });
    setInstantQuoteStep(1);
    setActiveSheet("instantQuote");
  }

  function toggleInstantQuoteScope(scope) {
    setInstantQuoteForm((prev) => {
      const current = Array.isArray(prev.quoteFor) ? prev.quoteFor : [];
      const next = current.includes(scope)
        ? current.filter((item) => item !== scope)
        : [...current, scope];
      return { ...prev, quoteFor: next };
    });
  }

  function goToInstantQuoteStep(nextStep) {
    if (instantQuoteStep === 0 && nextStep > instantQuoteStep) {
      if (!instantQuoteForm.phone.trim() && !instantQuoteForm.email.trim()) {
        showToast("Add a phone number or email.", "error");
        return;
      }
    }
    if (instantQuoteStep === 1 && nextStep > instantQuoteStep) {
      if (!Array.isArray(instantQuoteForm.quoteFor) || !instantQuoteForm.quoteFor.length) {
        showToast("Choose internal, external, or both.", "error");
        return;
      }
    }
    setInstantQuoteStep(Math.max(0, Math.min(2, nextStep)));
  }

  async function handleCreateInstantQuote(e) {
    e.preventDefault();
    if (!createOnsiteQuoteLead) {
      showToast("Quote creator is still loading.", "error");
      return;
    }
    if (!instantQuoteForm.phone.trim() && !instantQuoteForm.email.trim()) {
      showToast("Add a phone number or email.", "error");
      setInstantQuoteStep(0);
      return;
    }
    if (!Array.isArray(instantQuoteForm.quoteFor) || !instantQuoteForm.quoteFor.length) {
      showToast("Choose internal, external, or both.", "error");
      setInstantQuoteStep(1);
      return;
    }

    setCreatingQuoteLead(true);
    try {
      const result = await createOnsiteQuoteLead(instantQuoteForm);
      if (result?.ok && result.request?.id) {
        resetInstantQuoteForm();
        openLeadQuote(result.request.id);
      }
    } finally {
      setCreatingQuoteLead(false);
    }
  }

  async function handleCreateCustomer(e) {
    e.preventDefault();
    if (!createOnsiteQuoteLead) {
      showToast("Customer creator is still loading.", "error");
      return;
    }
    if (!customerForm.customer.trim()) {
      showToast("Add the customer name.", "error");
      return;
    }
    if (!customerForm.phone.trim() && !customerForm.email.trim()) {
      showToast("Add a phone number or email.", "error");
      return;
    }

    setCreatingCustomer(true);
    try {
      const result = await createOnsiteQuoteLead({
        ...customerForm,
        customerRecordOnly: true,
        quoteFor: ["Customer record"],
        projectType: "Customer record",
        notes: customerForm.notes || "Customer added from Home.",
      });
      if (result?.ok && result.request?.id) {
        setCustomerForm({
          customer: "",
          phone: "",
          email: "",
          address: "",
          suburb: "",
          postcode: "",
          trade: defaultTrade,
          notes: "",
        });
        showToast("Customer added.", "success");
        openLeadOverview(result.request.id);
      }
    } finally {
      setCreatingCustomer(false);
    }
  }

  async function copyShareLink() {
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      showToast("Link copied.", "success");
      window.setTimeout(() => setLinkCopied(false), 1800);
    } catch {
      showToast("Could not copy link.", "error");
    }
  }

  async function startNativeShare() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Request a quote",
          text: "Send your job details here and I will quote it.",
          url: shareLink,
        });
        return;
      } catch {
        return;
      }
    }
    copyShareLink();
  }

  function renderQuoteCustomerSplitSheet() {
    return (
      <ActionSheet
        title="New quote"
        subtitle="Who is this quote for?"
        onClose={closeSheet}
      >
        <div className="rq-choice-split">
          <button type="button" className="rq-choice-card orange" onClick={startNewCustomerQuote}>
            <span className="rq-choice-card__icon">
              <UserPlus size={28} />
            </span>
            <strong>New customer</strong>
            <small>Add customer details first.</small>
          </button>

          <button
            type="button"
            className="rq-choice-card slate"
            onClick={() => setActiveSheet("quoteExistingCustomer")}
          >
            <span className="rq-choice-card__icon">
              <Users size={28} />
            </span>
            <strong>Existing customer</strong>
            <small>Pick from saved customers.</small>
          </button>
        </div>

        <div className="rq-instant-quote-actions">
          <button
            type="button"
            className="rq-secondary-action"
            onClick={() => {
              setQuoteSheetTab("drafts");
              setActiveSheet("quote");
            }}
          >
            Draft quotes{draftQuoteLeads.length ? ` (${draftQuoteLeads.length})` : ""}
          </button>
        </div>
      </ActionSheet>
    );
  }

  function renderQuoteExistingCustomerSheet() {
    return (
      <ActionSheet
        title="Existing customer"
        subtitle="Pick a customer, then quote the next job."
        onClose={closeSheet}
      >
        {customerRows.length ? (
          <div className="rq-sheet-list">
            {customerRows.map((customer) => {
              const latest = customer.latestLead;
              const jobText = `${customer.jobs.length} job${customer.jobs.length === 1 ? "" : "s"}`;
              const contact = customer.email || customer.phone || leadMeta(latest);
              return (
                <JobRow
                  key={customer.key}
                  lead={latest}
                  businessId={businessId}
                  unlockedLeadIds={unlockedLeadIds}
                  title={customer.name}
                  sub={[jobText, contact].filter(Boolean).join(" - ")}
                  right="Use"
                  onClick={() => startExistingCustomerQuote(customer)}
                />
              );
            })}
          </div>
        ) : (
          <EmptySheetState
            title="No customers yet"
            body="Start with New customer for this quote."
          />
        )}

        <div className="rq-instant-quote-actions">
          <button
            type="button"
            className="rq-secondary-action"
            onClick={() => setActiveSheet("quoteCustomerSplit")}
          >
            Back
          </button>
          <button type="button" className="rq-share-primary" onClick={startNewCustomerQuote}>
            <UserPlus size={18} />
            New customer
          </button>
        </div>
      </ActionSheet>
    );
  }

  function renderInstantQuoteSheet() {
    const tradeOptions =
      Array.isArray(profile?.trade_types) && profile.trade_types.length
        ? profile.trade_types
        : [defaultTrade];
    const quoteForSummary = instantQuoteForm.quoteFor.length
      ? instantQuoteForm.quoteFor.join(", ")
      : "Choose internal/external";
    const scopeOptions = ["Internal", "External"];

    return (
      <ActionSheet
        title="New quote"
        subtitle={
          instantQuoteStep === 0
            ? "Add the customer."
            : instantQuoteStep === 1
              ? "Set the scope."
              : "Add notes last."
        }
        onClose={closeSheet}
      >
        <form className="rq-instant-quote-form" onSubmit={handleCreateInstantQuote}>
          <div className="rq-wizard-steps" aria-label="New quote progress">
            {["Customer", "Scope", "Notes"].map((label, index) => (
              <span key={label} className={index <= instantQuoteStep ? "active" : ""}>
                {label}
              </span>
            ))}
          </div>

          {instantQuoteStep === 0 && (
            <>
              <div className="rq-form-grid two">
                <label>
                  <span>Customer name</span>
                  <input
                    type="text"
                    value={instantQuoteForm.customer}
                    onChange={(e) => updateInstantQuoteField("customer", e.target.value)}
                    placeholder="Customer name"
                    autoComplete="name"
                  />
                </label>
                <label>
                  <span>Phone</span>
                  <input
                    type="tel"
                    value={instantQuoteForm.phone}
                    onChange={(e) => updateInstantQuoteField("phone", e.target.value)}
                    placeholder="Mobile number"
                    autoComplete="tel"
                  />
                </label>
              </div>

              <label>
                <span>Email</span>
                <input
                  type="email"
                  value={instantQuoteForm.email}
                  onChange={(e) => updateInstantQuoteField("email", e.target.value)}
                  placeholder="customer@email.com"
                  autoComplete="email"
                />
              </label>

              <label>
                <span>Job address</span>
                <input
                  type="text"
                  value={instantQuoteForm.address}
                  onChange={(e) => updateInstantQuoteField("address", e.target.value)}
                  placeholder="Street address"
                  autoComplete="street-address"
                />
              </label>

              <div className="rq-form-grid two">
                <label>
                  <span>Suburb</span>
                  <input
                    type="text"
                    value={instantQuoteForm.suburb}
                    onChange={(e) => updateInstantQuoteField("suburb", e.target.value)}
                    placeholder="Suburb"
                    autoComplete="address-level2"
                  />
                </label>
                <label>
                  <span>Postcode</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={instantQuoteForm.postcode}
                    onChange={(e) => updateInstantQuoteField("postcode", e.target.value)}
                    placeholder="Postcode"
                    autoComplete="postal-code"
                  />
                </label>
              </div>

              <label>
                <span>Trade</span>
                <select
                  value={instantQuoteForm.trade}
                  onChange={(e) => updateInstantQuoteField("trade", e.target.value)}
                >
                  {tradeOptions.map((trade) => (
                    <option key={trade} value={trade}>
                      {trade}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}

          {instantQuoteStep === 1 && (
            <>
              <details className="rq-multi-select" open>
                <summary>
                  <span>Quote for</span>
                  <strong>{quoteForSummary}</strong>
                </summary>
                <div className="rq-check-grid">
                  {scopeOptions.map((scope) => {
                    const checked = instantQuoteForm.quoteFor.includes(scope);
                    return (
                      <label key={scope} className={checked ? "checked" : ""}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleInstantQuoteScope(scope)}
                        />
                        <span>{scope}</span>
                      </label>
                    );
                  })}
                </div>
              </details>

              <label className="rq-toggle-row">
                <input
                  type="checkbox"
                  checked={instantQuoteForm.progressPayments}
                  onChange={(e) => updateInstantQuoteField("progressPayments", e.target.checked)}
                />
                <span>
                  <strong>Progress payments required</strong>
                  Send payment requests during the job.
                </span>
              </label>

              <label className="rq-toggle-row">
                <input
                  type="checkbox"
                  checked={instantQuoteForm.cashPayment}
                  onChange={(e) => updateInstantQuoteField("cashPayment", e.target.checked)}
                />
                <span>
                  <strong>Cash payment option</strong>
                  No GST invoice. Basic accept only.
                </span>
              </label>
            </>
          )}

          {instantQuoteStep === 2 && (
            <label>
              <span>Notes</span>
              <textarea
                value={instantQuoteForm.notes}
                onChange={(e) => updateInstantQuoteField("notes", e.target.value)}
                placeholder="Scope, prep, rooms, timing, access"
                rows={5}
                autoFocus
              />
            </label>
          )}

          <div className="rq-instant-quote-actions">
            {instantQuoteStep > 0 ? (
              <button
                type="button"
                className="rq-secondary-action"
                onClick={() => goToInstantQuoteStep(instantQuoteStep - 1)}
              >
                Back
              </button>
            ) : (
              <button
                type="button"
                className="rq-secondary-action"
                onClick={() => {
                  setQuoteSheetTab("drafts");
                  setActiveSheet("quote");
                }}
              >
                Draft quotes{draftQuoteLeads.length ? ` (${draftQuoteLeads.length})` : ""}
              </button>
            )}

            {instantQuoteStep < 2 ? (
              <button
                type="button"
                className="rq-share-primary"
                onClick={() => goToInstantQuoteStep(instantQuoteStep + 1)}
              >
                Next
                <ChevronRight size={18} />
              </button>
            ) : (
              <button type="submit" className="rq-share-primary" disabled={creatingQuoteLead}>
                <FileText size={18} />
                {creatingQuoteLead ? "Creating..." : "Start quote"}
              </button>
            )}
          </div>
        </form>
      </ActionSheet>
    );
  }

  function renderQuoteSheet() {
    const rows = quoteSheetTab === "ready" ? readyQuoteLeads : draftQuoteLeads;
    return (
      <ActionSheet
        title="New quote"
        subtitle="Pick a job, then build or finish the quote."
        onClose={closeSheet}
      >
        <div className="rq-sheet-tabs" role="tablist" aria-label="Quote jobs">
          <button
            type="button"
            className={quoteSheetTab === "ready" ? "active" : ""}
            onClick={() => setQuoteSheetTab("ready")}
          >
            Ready to quote <span>{readyQuoteLeads.length}</span>
          </button>
          <button
            type="button"
            className={quoteSheetTab === "drafts" ? "active" : ""}
            onClick={() => setQuoteSheetTab("drafts")}
          >
            Draft quotes <span>{draftQuoteLeads.length}</span>
          </button>
        </div>
        {rows.length ? (
          <div className="rq-sheet-list">
            {rows.map((lead) => (
              <JobRow
                key={lead.id}
                lead={lead}
                businessId={businessId}
                unlockedLeadIds={unlockedLeadIds}
                right={quoteSheetTab === "drafts" ? "Draft" : leadDate(lead)}
                onClick={() => openLeadQuote(lead.id)}
              />
            ))}
          </div>
        ) : (
          <EmptySheetState
            title={quoteSheetTab === "drafts" ? "No draft quotes" : "No jobs waiting"}
            body={
              quoteSheetTab === "drafts"
                ? "Drafts you save will stay here until they are sent."
                : "New customer jobs will appear here when they are ready to quote."
            }
          />
        )}
      </ActionSheet>
    );
  }

  function renderShareSheet() {
    return (
      <ActionSheet
        title="Send remote quote"
        subtitle="Send this to a customer so their job lands in your job board."
        onClose={closeSheet}
      >
        <div className="rq-share-panel">
          <button type="button" className="rq-share-primary" onClick={startNativeShare}>
            <Send size={18} />
            Share from this device
          </button>
          <div className="rq-share-grid">
            <a href={`sms:?body=${shareCopy}`}>SMS</a>
            <a href={`https://wa.me/?text=${shareCopy}`} target="_blank" rel="noreferrer">
              WhatsApp
            </a>
            <a href={`mailto:?subject=${encodeURIComponent("Quote request")}&body=${shareCopy}`}>
              Email
            </a>
            <button type="button" onClick={copyShareLink}>
              {linkCopied ? <Check size={15} /> : <Clipboard size={15} />}
              {linkCopied ? "Copied" : "Copy"}
            </button>
          </div>
          <div className="rq-share-url">{shareLink}</div>
        </div>
      </ActionSheet>
    );
  }

  function renderPaperworkSheet() {
    const rows = paperworkTab === "quotes"
      ? sentQuoteLeads
      : paperworkTab === "drafts"
        ? draftQuoteLeads
        : invoiceRowsWithLead;

    return (
      <ActionSheet
        title="Existing quotes & invoices"
        subtitle="Open a saved quote, finish a draft, or check an invoice."
        onClose={closeSheet}
      >
        <div className="rq-sheet-tabs three" role="tablist" aria-label="Existing quotes and invoices">
          <button
            type="button"
            className={paperworkTab === "quotes" ? "active" : ""}
            onClick={() => setPaperworkTab("quotes")}
          >
            Sent <span>{sentQuoteLeads.length}</span>
          </button>
          <button
            type="button"
            className={paperworkTab === "drafts" ? "active" : ""}
            onClick={() => setPaperworkTab("drafts")}
          >
            Drafts <span>{draftQuoteLeads.length}</span>
          </button>
          <button
            type="button"
            className={paperworkTab === "invoices" ? "active" : ""}
            onClick={() => setPaperworkTab("invoices")}
          >
            Invoices <span>{invoiceRowsWithLead.length}</span>
          </button>
        </div>

        {rows.length ? (
          <div className="rq-sheet-list">
            {paperworkTab === "invoices"
              ? rows.map(({ invoice, lead }) => (
                  <JobRow
                    key={invoice.id}
                    lead={lead}
                    businessId={businessId}
                    unlockedLeadIds={unlockedLeadIds}
                    title={invoice.invoice_number || "Invoice"}
                    sub={
                      lead
                        ? `${leadName(lead, businessId, unlockedLeadIds)} - ${leadMeta(lead)}`
                        : "Job details loading"
                    }
                    right={`${formatMoneyDisplay(invoice.total || 0)} - ${invoice.status || "draft"}`}
                    onClick={() => openLeadInvoice(invoice.lead_id)}
                  />
                ))
              : rows.map((lead) => (
                  <JobRow
                    key={lead.id}
                    lead={lead}
                    businessId={businessId}
                    unlockedLeadIds={unlockedLeadIds}
                    title={lead.quote?.quote_number || (paperworkTab === "drafts" ? "Draft quote" : "Quote")}
                    sub={`${leadName(lead, businessId, unlockedLeadIds)} - ${leadMeta(lead)}`}
                    right={`${formatMoneyDisplay(lead.quote?.total || 0)} - ${
                      paperworkTab === "drafts"
                        ? "draft"
                        : lead.quote?.customer_status || lead.quote?.status || "sent"
                    }`}
                    onClick={() => openLeadQuote(lead.id)}
                  />
                ))}
          </div>
        ) : (
          <EmptySheetState
            title={
              paperworkTab === "quotes"
                ? "No sent quotes yet"
                : paperworkTab === "drafts"
                  ? "No draft quotes"
                  : "No invoices yet"
            }
            body={
              paperworkTab === "quotes"
                ? "Quotes you send to customers will show up here."
                : paperworkTab === "drafts"
                  ? "Drafts you save will stay here until they are sent."
                  : "When you send an invoice from a job, it will show up here."
            }
          />
        )}
      </ActionSheet>
    );
  }

  function renderMessagesSheet() {
    return (
      <ActionSheet
        title="Messages"
        subtitle="All job conversations in one inbox."
        onClose={closeSheet}
      >
        {messageThreads.length ? (
          <div className="rq-thread-list">
            {messageThreads.map(({ lead, message }) => {
              const unread = message.sender_role !== "tradie" && !isRead(lead.id);
              return (
                <button
                  key={lead.id}
                  type="button"
                  className={`rq-thread-row ${unread ? "unread" : ""}`}
                  onClick={() => openLeadMessages(lead.id)}
                >
                  <span className="rq-thread-row__avatar">
                    {leadName(lead, businessId, unlockedLeadIds).slice(0, 1).toUpperCase()}
                  </span>
                  <span className="rq-thread-row__body">
                    <strong>{leadName(lead, businessId, unlockedLeadIds)}</strong>
                    <small>{messagePreview(message)}</small>
                  </span>
                  <span className="rq-thread-row__meta">{leadMeta(lead)}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <EmptySheetState
            title="No messages yet"
            body="Customer replies and job chats will appear here."
          />
        )}
      </ActionSheet>
    );
  }

  function renderMediaSheet() {
    return (
      <ActionSheet
        title="Pictures and media"
        subtitle="Pick a job to view the customer photos and saved job media."
        onClose={closeSheet}
      >
        {mediaJobs.length ? (
          <div className="rq-sheet-list">
            {mediaJobs.map((lead) => {
              const count =
                Math.max(
                  Number(lead.photos || 0),
                  Number(lead.form?.uploadedPhotos?.length || 0)
                ) +
                (Array.isArray(lead.tradieNotesPhotos)
                  ? lead.tradieNotesPhotos.length
                  : 0);
              return (
                <JobRow
                  key={lead.id}
                  lead={lead}
                  businessId={businessId}
                  unlockedLeadIds={unlockedLeadIds}
                  right={`${count} file${count === 1 ? "" : "s"}`}
                  onClick={() => openLeadMedia(lead.id)}
                />
              );
            })}
          </div>
        ) : (
          <EmptySheetState
            title="No job media yet"
            body="Photos from customers and notes photos will appear here by job."
          />
        )}
      </ActionSheet>
    );
  }

  function renderNewCustomerSheet() {
    return (
      <ActionSheet
        title="New customer"
        subtitle="Create a customer record before the next job."
        onClose={closeSheet}
      >
        <form className="rq-instant-quote-form" onSubmit={handleCreateCustomer}>
          <div className="rq-form-grid two">
            <label>
              <span>Customer name</span>
              <input
                type="text"
                value={customerForm.customer}
                onChange={(e) => updateCustomerField("customer", e.target.value)}
                placeholder="Customer name"
                autoComplete="name"
              />
            </label>
            <label>
              <span>Phone</span>
              <input
                type="tel"
                value={customerForm.phone}
                onChange={(e) => updateCustomerField("phone", e.target.value)}
                placeholder="Mobile number"
                autoComplete="tel"
              />
            </label>
          </div>

          <label>
            <span>Email</span>
            <input
              type="email"
              value={customerForm.email}
              onChange={(e) => updateCustomerField("email", e.target.value)}
              placeholder="customer@email.com"
              autoComplete="email"
            />
          </label>

          <label>
            <span>Default address</span>
            <input
              type="text"
              value={customerForm.address}
              onChange={(e) => updateCustomerField("address", e.target.value)}
              placeholder="Street address"
              autoComplete="street-address"
            />
          </label>

          <div className="rq-form-grid two">
            <label>
              <span>Suburb</span>
              <input
                type="text"
                value={customerForm.suburb}
                onChange={(e) => updateCustomerField("suburb", e.target.value)}
                placeholder="Suburb"
                autoComplete="address-level2"
              />
            </label>
            <label>
              <span>Postcode</span>
              <input
                type="text"
                inputMode="numeric"
                value={customerForm.postcode}
                onChange={(e) => updateCustomerField("postcode", e.target.value)}
                placeholder="Postcode"
                autoComplete="postal-code"
              />
            </label>
          </div>

          <label>
            <span>Notes</span>
            <textarea
              value={customerForm.notes}
              onChange={(e) => updateCustomerField("notes", e.target.value)}
              placeholder="Builder, site contact, access details, repeat jobs"
              rows={4}
            />
          </label>

          <div className="rq-instant-quote-actions">
            <button
              type="button"
              className="rq-secondary-action"
              onClick={() => setActiveSheet("customers")}
            >
              Back
            </button>
            <button type="submit" className="rq-share-primary" disabled={creatingCustomer}>
              <UserPlus size={18} />
              {creatingCustomer ? "Creating..." : "Create customer"}
            </button>
          </div>
        </form>
      </ActionSheet>
    );
  }

  function renderCustomersSheet() {
    return (
      <ActionSheet
        title="Customers"
        subtitle="Every customer with a job in your system."
        onClose={closeSheet}
      >
        <div className="rq-sheet-toolbar">
          <button type="button" className="rq-share-primary" onClick={() => setActiveSheet("newCustomer")}>
            <UserPlus size={18} />
            New customer
          </button>
        </div>
        {customerRows.length ? (
          <div className="rq-sheet-list">
            {customerRows.map((customer) => {
              const latest = customer.latestLead;
              const jobText = `${customer.jobs.length} job${customer.jobs.length === 1 ? "" : "s"}`;
              const contact = customer.email || customer.phone || leadMeta(latest);
              return (
                <JobRow
                  key={customer.key}
                  lead={latest}
                  businessId={businessId}
                  unlockedLeadIds={unlockedLeadIds}
                  title={customer.name}
                  sub={[jobText, contact].filter(Boolean).join(" - ")}
                  right={leadDate(latest)}
                  onClick={() => openLeadOverview(latest.id)}
                />
              );
            })}
          </div>
        ) : (
          <EmptySheetState
            title="No customers yet"
            body="Customers will appear here after jobs are created or quoted."
          />
        )}
      </ActionSheet>
    );
  }

  return (
    <div className="rq-launcher rq-launcher--icons-only">
      <section className="rq-launcher-grid" aria-label="Main actions">
        <LauncherTile
          icon={FileText}
          title="New quote"
          sub="Start or finish"
          badge={
            draftQuoteLeads.length
              ? `${draftQuoteLeads.length} draft`
              : readyQuoteLeads.length
                ? String(readyQuoteLeads.length)
                : ""
          }
          tone="orange"
          onClick={() => {
            setInstantQuoteStep(0);
            setActiveSheet("quoteCustomerSplit");
          }}
        />
        <LauncherTile
          icon={Send}
          title="Send remote quote"
          sub="Share job form"
          tone="green"
          onClick={() => setActiveSheet("share")}
        />
        <LauncherTile
          icon={ReceiptText}
          title="Quotes & invoices"
          sub="Existing docs"
          badge={paperworkAlertCount ? String(paperworkAlertCount) : ""}
          tone="blue"
          onClick={() => setActiveSheet("paperwork")}
        />
        <LauncherTile
          icon={MessageCircle}
          title="Messages"
          sub="All job chats"
          badge={unreadThreads.length ? String(unreadThreads.length) : ""}
          tone="purple"
          onClick={() => setActiveSheet("messages")}
        />
        <LauncherTile
          icon={Users}
          title="Customers"
          sub="All customers"
          badge={customerRows.length ? String(customerRows.length) : ""}
          tone="slate"
          onClick={() => setActiveSheet("customers")}
        />
        <LauncherTile
          icon={Images}
          title="Pictures/media"
          sub="By job"
          badge={mediaJobs.length ? String(mediaJobs.length) : ""}
          tone="teal"
          onClick={() => setActiveSheet("media")}
        />
        <LauncherTile
          icon={KanbanSquare}
          title="Job board"
          sub="All active jobs"
          badge={activeJobCount ? String(activeJobCount) : ""}
          tone="dark"
          onClick={onSwitchToPipeline}
        />
      </section>

      {activeSheet === "quoteCustomerSplit" ? renderQuoteCustomerSplitSheet() : null}
      {activeSheet === "quoteExistingCustomer" ? renderQuoteExistingCustomerSheet() : null}
      {activeSheet === "instantQuote" ? renderInstantQuoteSheet() : null}
      {activeSheet === "quote" ? renderQuoteSheet() : null}
      {activeSheet === "share" ? renderShareSheet() : null}
      {activeSheet === "paperwork" ? renderPaperworkSheet() : null}
      {activeSheet === "messages" ? renderMessagesSheet() : null}
      {activeSheet === "customers" ? renderCustomersSheet() : null}
      {activeSheet === "newCustomer" ? renderNewCustomerSheet() : null}
      {activeSheet === "media" ? renderMediaSheet() : null}
    </div>
  );
}
